import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ProgressStepsCompact } from '@/components/ui/progress-steps';
import { Package, Send, CheckCircle, Clock, Mail, MessageCircle, RefreshCw, PenTool, User, Eraser, History, Eye, Calendar, Search, Filter, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/utils/dateFormat';
import { useAuth } from '@/contexts/AuthContext';
import { useFuncionarios, FuncionarioSelect } from '@/components/FuncionarioSelect';

export default function ArsenalPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'administrador'].includes(user?.perfil || user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [responsavelNome, setResponsavelNome] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllHistory, setShowAllHistory] = useState(false);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const { funcionarios } = useFuncionarios();
  const [arsenalFuncId, setArsenalFuncId] = useState('');
  const [arsenalFuncNome, setArsenalFuncNome] = useState('');

  useEffect(() => {
    fetchArsenal();
  }, []);

  // Initialize canvas with callback ref
  const initCanvas = useCallback((canvas) => {
    if (canvas) {
      canvasRef.current = canvas;
      canvas.width = 400;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      contextRef.current = ctx;
      setCanvasReady(true);
    }
  }, []);

  // Reset canvas ready state when dialog closes
  useEffect(() => {
    if (!showSignatureDialog) {
      setCanvasReady(false);
      contextRef.current = null;
    }
  }, [showSignatureDialog]);

  const fetchArsenal = async () => {
    try {
      const response = await api.get('/arsenal');
      setItems(response.data);
    } catch (error) {
      toast.error('Erro ao carregar arsenal');
    } finally {
      setLoading(false);
    }
  };

  const handleNotify = async (item) => {
    try {
      const response = await api.post(`/arsenal/${item.id}/notify`);
      if (response.data.notifications.email) {
        toast.success('Email enviado com sucesso!');
      }
      if (response.data.notifications.whatsapp_link) {
        window.open(response.data.notifications.whatsapp_link, '_blank');
        toast.success('Abrindo WhatsApp...');
      }
      fetchArsenal();
    } catch (error) {
      toast.error('Erro ao enviar notificação');
    }
  };

  const openSignatureDialog = (item) => {
    setSelectedItem(item);
    setResponsavelNome('');
    setArsenalFuncId('');
    setArsenalFuncNome('');
    setShowSignatureDialog(true);
  };

  const clearCanvas = () => {
    if (canvasRef.current && contextRef.current) {
      const canvas = canvasRef.current;
      contextRef.current.fillStyle = 'white';
      contextRef.current.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  const startDrawing = (e) => {
    if (!canvasRef.current || !contextRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current || !contextRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    contextRef.current.lineTo(x, y);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const handleSignatureSubmit = async () => {
    if (!responsavelNome.trim()) {
      toast.error('Informe o nome do responsável pela retirada');
      return;
    }
    if (!arsenalFuncId) {
      toast.error('Selecione o funcionário responsável pela retirada');
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    // Check if canvas is empty (all white)
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isBlank = imageData.data.every((val, idx) => idx % 4 === 3 || val === 255);
    
    if (isBlank) {
      toast.error('Por favor, assine no campo de assinatura');
      return;
    }

    try {
      await api.put(`/arsenal/${selectedItem.id}/mark-picked-with-signature`, {
        assinatura_base64: signatureData,
        responsavel_nome: responsavelNome,
        funcionario_id: arsenalFuncId,
        funcionario_nome: arsenalFuncNome
      });
      toast.success('Material retirado com assinatura digital!');
      setShowSignatureDialog(false);
      fetchArsenal();
    } catch (error) {
      toast.error('Erro ao registrar retirada');
    }
  };

  const handleMarkPickedSimple = async (itemId) => {
    try {
      await api.put(`/arsenal/${itemId}/mark-picked`);
      toast.success('Material marcado como retirado!');
      fetchArsenal();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteArsenalItem = async (itemId) => {
    if (!window.confirm('Deseja excluir este item do arsenal?')) return;
    try {
      await api.delete(`/arsenal/${itemId}`);
      toast.success('Item excluído do arsenal');
      fetchArsenal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir item');
    }
  };

  const openHistoryDetail = (item) => {
    setSelectedHistoryItem(item);
    setShowHistoryDialog(true);
  };

  // Filter retirados by search term
  const filteredRetirados = items
    .filter(i => i.status === 'retirado')
    .filter(i => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        i.cliente?.nome?.toLowerCase().includes(search) ||
        i.responsavel_retirada?.toLowerCase().includes(search) ||
        i.materiais?.some(m => m.nome?.toLowerCase().includes(search) || m.codigo?.toLowerCase().includes(search))
      );
    })
    .sort((a, b) => new Date(b.data_retirada) - new Date(a.data_retirada));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const aguardando = items.filter(i => i.status === 'aguardando_retirada');

  return (
    <div className="space-y-6" data-testid="arsenal-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Arsenal de Distribuição</h1>
          <p className="text-slate-500">Materiais esterilizados aguardando retirada</p>
        </div>
        <Button onClick={fetchArsenal} variant="outline" title="Atualizar lista" data-testid="refresh-arsenal-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Aguardando Retirada */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-yellow-600" />
          Aguardando Retirada ({aguardando.length})
        </h2>
        
        {aguardando.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Nenhum material aguardando retirada</p>
        ) : (
          <div className="grid gap-4">
            {aguardando.map((item) => (
              <div key={item.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50" data-testid={`arsenal-item-${item.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{item.cliente?.nome || 'Cliente'}</h3>
                      <ProgressStepsCompact currentStatus="no_arsenal" />
                    </div>
                    <p className="text-sm text-slate-500">{item.cliente?.telefone}</p>
                    <p className="text-sm text-slate-500">{item.cliente?.email}</p>
                    <div className="mt-2">
                      <p className="text-xs text-slate-400">Materiais:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.materiais?.map((mat, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                            {mat.codigo} - {mat.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Entrada: {formatDateTime(item.data_entrada)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-1">
                      {item.notificacao_email && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Enviado
                        </span>
                      )}
                      {item.notificacao_whatsapp && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" /> Enviado
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleNotify(item)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      title="Enviar notificação por Email e WhatsApp"
                      data-testid={`notify-btn-${item.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Notificar Cliente
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openSignatureDialog(item)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      title="Registrar retirada com assinatura digital"
                      data-testid={`signature-btn-${item.id}`}
                    >
                      <PenTool className="w-4 h-4 mr-1" />
                      Retirar c/ Assinatura
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkPickedSimple(item.id)}
                      title="Marcar como retirado (sem assinatura)"
                      data-testid={`mark-picked-btn-${item.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Marcar Retirado
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteArsenalItem(item.id)} data-testid={`btn-excluir-arsenal-${item.id}`}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Excluir
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Histórico de Entregas */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <History className="w-5 h-5 text-green-600" />
            Histórico de Entregas ({filteredRetirados.length})
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por cliente, responsável ou material..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
                data-testid="history-search"
              />
            </div>
          </div>
        </div>

        {filteredRetirados.length === 0 ? (
          <p className="text-slate-500 text-center py-8">
            {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma entrega registrada'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Cliente</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Materiais</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Retirado por</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Data/Hora</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Assinatura</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(showAllHistory ? filteredRetirados : filteredRetirados.slice(0, 10)).map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50" data-testid={`history-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{item.cliente?.nome || 'N/A'}</div>
                        <div className="text-xs text-slate-500">{item.cliente?.telefone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {item.materiais?.slice(0, 2).map((mat, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                              {mat.codigo}
                            </span>
                          ))}
                          {item.materiais?.length > 2 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                              +{item.materiais.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-900">{item.responsavel_retirada || 'Não informado'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {item.data_retirada ? formatDateTime(item.data_retirada) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.assinatura_digital ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                            <PenTool className="w-3 h-3" />
                            Assinado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 rounded text-xs">
                            Sem assinatura
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openHistoryDetail(item)}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`view-detail-btn-${item.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredRetirados.length > 10 && (
              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  className="text-slate-600"
                >
                  {showAllHistory ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Mostrar menos
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Mostrar todos ({filteredRetirados.length})
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* History Detail Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-green-600" />
              Detalhes da Entrega
            </DialogTitle>
          </DialogHeader>

          {selectedHistoryItem && (
            <div className="space-y-4">
              {/* Cliente Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">Informações do Cliente</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Nome:</span>
                    <span className="ml-2 font-medium">{selectedHistoryItem.cliente?.nome || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Telefone:</span>
                    <span className="ml-2">{selectedHistoryItem.cliente?.telefone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <span className="ml-2">{selectedHistoryItem.cliente?.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">CPF/CNPJ:</span>
                    <span className="ml-2">{selectedHistoryItem.cliente?.cpf_cnpj || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Materiais */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">Materiais Entregues</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedHistoryItem.materiais?.map((mat, idx) => (
                    <span key={idx} className="px-3 py-1 bg-white border border-blue-200 rounded-lg text-sm">
                      <span className="font-medium text-blue-700">{mat.codigo}</span>
                      <span className="text-slate-600 ml-1">- {mat.nome}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Retirada Info */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">Informações da Retirada</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Retirado por:</span>
                    <span className="ml-2 font-medium text-green-700">
                      {selectedHistoryItem.responsavel_retirada || 'Não informado'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Data/Hora:</span>
                    <span className="ml-2">
                      {selectedHistoryItem.data_retirada
                        ? formatDateTime(selectedHistoryItem.data_retirada)
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Data de Entrada:</span>
                    <span className="ml-2">
                      {selectedHistoryItem.data_entrada
                        ? formatDateTime(selectedHistoryItem.data_entrada)
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Notificações:</span>
                    <span className="ml-2">
                      {selectedHistoryItem.notificacao_email && <span className="text-green-600 mr-2">📧 Email</span>}
                      {selectedHistoryItem.notificacao_whatsapp && <span className="text-green-600">📱 WhatsApp</span>}
                      {!selectedHistoryItem.notificacao_email && !selectedHistoryItem.notificacao_whatsapp && 
                        <span className="text-slate-400">Nenhuma</span>}
                    </span>
                  </div>
                </div>
              </div>

              {/* Assinatura Digital */}
              {selectedHistoryItem.assinatura_digital && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <PenTool className="w-4 h-4 text-amber-600" />
                    Assinatura Digital
                  </h3>
                  <div className="bg-white border border-amber-200 rounded-lg p-2 inline-block">
                    <img
                      src={selectedHistoryItem.assinatura_digital}
                      alt="Assinatura Digital"
                      className="max-h-32"
                      style={{ maxWidth: '100%' }}
                    />
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    Assinatura registrada digitalmente no momento da retirada
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-blue-600" />
              Assinatura Digital - Retirada de Material
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedItem && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>Cliente:</strong> {selectedItem.cliente?.nome}</p>
                <p><strong>Materiais:</strong> {selectedItem.materiais?.map(m => m.nome).join(', ')}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="responsavel">Nome do Responsável pela Retirada *</Label>
              <Input
                id="responsavel"
                value={responsavelNome}
                onChange={(e) => setResponsavelNome(e.target.value)}
                placeholder="Digite o nome completo"
                data-testid="responsavel-input"
              />
            </div>

            {/* Funcionário Responsável */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <FuncionarioSelect
                value={arsenalFuncId}
                onChange={(id, nome) => { setArsenalFuncId(id); setArsenalFuncNome(nome); }}
                funcionarios={funcionarios}
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Assinatura *</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearCanvas}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="clear-signature-btn"
                >
                  <Eraser className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
              </div>
              <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white">
                <canvas
                  ref={initCanvas}
                  className="w-full cursor-crosshair touch-none"
                  style={{ height: '200px' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              {!canvasReady && (
                <p className="text-xs text-amber-600 text-center">Carregando área de assinatura...</p>
              )}
              <p className="text-xs text-slate-500 text-center">
                Use o mouse ou o dedo para assinar
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignatureDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSignatureSubmit}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-signature-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Confirmar Retirada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
